// mobile_v1: responsive — form 1 col + cards แทนตาราง
import React, { useState, useEffect } from 'react';
import API from '../api';
import { Gamepad2, Plus, Edit2, Trash2, Save, X, Power } from 'lucide-react';
import toast from 'react-hot-toast';

/* hotgames_media_v1 */ const empty = { provider:'', name:'', rtp:'', rtp_min:85, rtp_max:99, description:'', is_active:true, sort_order:0, image_url:'', play_url:'' };

/* game_providers_v1: รายชื่อค่ายย้ายไปอยู่ในฐานข้อมูลแล้ว (ตาราง game_providers)
   โหลดจาก /api/game-providers = ค่ายกลางที่ผู้ให้บริการกำหนด + ค่ายที่ร้านนี้เพิ่มเอง
   เดิมฝังไว้ในโค้ด 8 ค่าย เพิ่มค่ายใหม่ต้องแก้โค้ดแล้ว deploy ซึ่งช้ากว่าที่ค่ายใหม่จะเกิด */
const OTHER = '__other__';

export default function HotGames() {
  const [games, setGames] = useState([]);
  const [form, setForm] = useState(empty);
  const [editId, setEditId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  const load = async () => {
    const { data } = await API.get('/api/hotgames');
    setGames(data);
  };

  /* game_providers_v1 */
  const [providers, setProviders] = useState([]);
  const loadProviders = async () => {
    try {
      const { data } = await API.get('/api/game-providers');
      setProviders(data.providers || []);
    } catch { setProviders([]); }
  };

  useEffect(() => { load(); loadProviders(); }, []);

  /* ค่ายที่เคยพิมพ์ไว้ในเกมแต่ยังไม่ได้อยู่ในตาราง (ข้อมูลเก่าก่อนย้ายระบบ) ต้องยังเลือกได้
     ไม่งั้นพอกดแก้เกมเดิม ค่ายจะหายไปจากตัวเลือกเฉยๆ */
  const knownProviders = React.useMemo(() => {
    const fromApi = providers.map(p => p.name);
    const used = games.map(g => String(g.provider || '').trim()).filter(Boolean);
    return Array.from(new Set([...fromApi, ...used]));
  }, [providers, games]);
  const [otherMode, setOtherMode] = useState(false);

  const handleSave = async () => {
    const provider = String(form.provider || '').trim(); // hotgames_provider_free_v1: กันเว้นวรรคหัวท้ายทำให้ค่ายเดียวกันกลายเป็นสองค่าย
    if (!provider || !form.name || !form.rtp) { toast.error('กรุณากรอกข้อมูลให้ครบ'); return; }
    setLoading(true);
    try {
      const payload = { ...form, provider };
      if (editId) { await API.put(`/api/hotgames/${editId}`, payload); toast.success('อัปเดตสำเร็จ'); }
      else { await API.post('/api/hotgames', payload); toast.success('เพิ่มเกมสำเร็จ'); }
      setForm(empty); setEditId(null); setOtherMode(false); setShowForm(false);
      load(); loadProviders(); /* game_providers_v1: ค่ายที่เพิ่งพิมพ์ใหม่ถูกจำไว้ฝั่งเซิร์ฟเวอร์ ดึงกลับมาให้ตัวเลือกอัปเดตทันที */
    } catch { toast.error('เกิดข้อผิดพลาด'); }
    setLoading(false);
  };

  const handleEdit = (g) => {
    setEditId(g.id);
    /* hotgames_media_v1 */ setForm({ provider:g.provider, name:g.name, rtp:g.rtp, rtp_min:g.rtp_min||85, rtp_max:g.rtp_max||99, description:g.description||'', is_active:g.is_active, sort_order:g.sort_order||0, image_url:g.image_url||'', play_url:g.play_url||'' });
    /* ค่ายที่ไม่อยู่ในรายการ (เช่นของเก่าที่บันทึกไว้ผิด) ต้องเปิดช่องพิมพ์ให้แก้ได้ */
    setOtherMode(!!g.provider && !knownProviders.includes(String(g.provider).trim()));
    setShowForm(true);
    window.scrollTo({ top:0, behavior:'smooth' });
  };

  const handleDelete = async (id) => {
    if (!window.confirm('ลบเกมนี้?')) return;
    await API.delete(`/api/hotgames/${id}`);
    toast.success('ลบแล้ว'); load();
  };

  const handleToggle = async (g) => {
    await API.put(`/api/hotgames/${g.id}`, { ...g, is_active: !g.is_active });
    load();
  };

  const getRtpBar = (rtp) => {
    const n = parseFloat(rtp);
    const filled = Math.min(10, Math.max(1, Math.floor((n - 90) * 1.2) + 7));
    return '🟩'.repeat(filled) + '⬜'.repeat(10 - filled);
  };

  const providerColor = (p) => {
    p = String(p || ''); // hotgames_provider_free_v1: ค่ายเป็นข้อความอิสระแล้ว อาจว่างได้
    if (p.includes('PG')) return { bg:'#fef3c7', color:'#92400e' };
    if (p.includes('Pragmatic')) return { bg:'#dbeafe', color:'#1e40af' };
    if (p.includes('Relax')) return { bg:'#d1fae5', color:'#065f46' };
    return { bg:'#f3e8ff', color:'#6b21a8' };
  };

  const filtered = games.filter(g => !search || String(g.name || '').includes(search) || String(g.provider || '').includes(search));
  const active = games.filter(g => g.is_active).length;

  return (
    <div style={{ padding: isMobile ? '16px 12px' : '24px 28px', background:'#f8fafc', minHeight:'100%' }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        alignItems: isMobile ? 'stretch' : 'center',
        justifyContent: 'space-between',
        gap: isMobile ? 12 : 0,
        marginBottom: isMobile ? 16 : 24,
      }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <Gamepad2 size={isMobile ? 22 : 26} color="#0ea5e9" />
          <div>
            <h1 style={{ margin:0, fontSize: isMobile ? 18 : 22, fontWeight:700, color:'#1e293b' }}>จัดการเกม</h1>
            <p style={{ margin:0, fontSize: isMobile ? 12 : 13, color:'#64748b' }}>
              ({active} active / {games.length} ทั้งหมด)
            </p>
          </div>
        </div>
        <button onClick={() => { setForm(empty); setEditId(null); setOtherMode(false); setShowForm(true); }}
          style={{
            display:'flex', alignItems:'center', justifyContent: 'center', gap:6,
            background:'#0ea5e9', color:'#fff', border:'none', borderRadius:8,
            padding: isMobile ? '12px 18px' : '10px 18px',
            fontSize: isMobile ? 14 : 13, fontWeight:600, cursor:'pointer',
          }}>
          <Plus size={isMobile ? 16 : 15} />เพิ่มเกม
        </button>
      </div>

      {/* Form */}
      {showForm && (
        <div style={{
          background:'#fff', borderRadius:14,
          padding: isMobile ? 16 : 24,
          boxShadow:'0 1px 6px rgba(0,0,0,0.07)',
          marginBottom: isMobile ? 16 : 24,
        }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom: 16 }}>
            <h2 style={{ margin:0, fontSize: isMobile ? 14 : 15, fontWeight:600, color:'#1e293b' }}>
              {editId ? '✏️ แก้ไขเกม' : '➕ เพิ่มเกมใหม่'}
            </h2>
            <button onClick={() => { setShowForm(false); setEditId(null); setOtherMode(false); }}
              style={{ background:'none', border:'none', cursor:'pointer', padding: 4 }}>
              <X size={20} color="#94a3b8" />
            </button>
          </div>
          <div style={{
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 2fr 1fr 1fr 1fr 1fr',
            gap: 12, marginBottom: 12,
          }}>
            <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
              <Label>ค่าย</Label>
              {/* hotgames_provider_free_v1: เดิมตัวเลือก "อื่นๆ" มีค่าเป็น "other"
                  เลือกแล้วชื่อค่ายจะถูกบันทึกเป็นคำว่า other ตรงๆ ใช้งานไม่ได้จริง
                  ตอนนี้เลือกแล้วเปิดช่องให้พิมพ์ชื่อค่ายเองแทน */}
              <select
                value={otherMode ? OTHER : form.provider}
                onChange={e => {
                  const v = e.target.value;
                  if (v === OTHER) { setOtherMode(true); setForm({ ...form, provider: '' }); }
                  else { setOtherMode(false); setForm({ ...form, provider: v }); }
                }}
                style={inputStyle(isMobile)}>
                <option value="">เลือกค่าย</option>
                {knownProviders.map(p => <option key={p} value={p}>{p}</option>)}
                <option value={OTHER}>+ เพิ่มค่ายใหม่...</option>
              </select>
              {otherMode && (
                <input
                  value={form.provider}
                  onChange={e => setForm({ ...form, provider: e.target.value })}
                  placeholder="พิมพ์ชื่อค่าย เช่น Joker, Habanero"
                  maxLength={60}
                  autoFocus
                  style={{ ...inputStyle(isMobile), marginTop: 8 }} />
              )}
            </div>
            <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
              <Label>ชื่อเกม</Label>
              <input value={form.name} onChange={e => setForm({...form, name:e.target.value})}
                placeholder="เช่น Mahjong Ways"
                style={inputStyle(isMobile)} />
            </div>
            <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
              <Label>RTP (อ้างอิง)</Label>
              <input value={form.rtp} onChange={e => setForm({...form, rtp:e.target.value})}
                placeholder="96.95%"
                style={inputStyle(isMobile)} />
            </div>
            <div>
              <Label color="#0ea5e9">🎲 Min %</Label>
              <input type="number" min="1" max="99" value={form.rtp_min}
                onChange={e => setForm({...form, rtp_min:parseInt(e.target.value)||85})}
                style={{ ...inputStyle(isMobile), border: '2px solid #bae6fd' }} />
            </div>
            <div>
              <Label color="#0ea5e9">🎲 Max %</Label>
              <input type="number" min="1" max="100" value={form.rtp_max}
                onChange={e => setForm({...form, rtp_max:parseInt(e.target.value)||99})}
                style={{ ...inputStyle(isMobile), border: '2px solid #bae6fd' }} />
            </div>
            <div style={{ gridColumn: isMobile ? '1 / -1' : 'auto' }}>
              <Label>ลำดับ</Label>
              <input type="number" value={form.sort_order}
                onChange={e => setForm({...form, sort_order:parseInt(e.target.value)||0})}
                style={inputStyle(isMobile)} />
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <Label>คำอธิบาย</Label>
            <input value={form.description} onChange={e => setForm({...form, description:e.target.value})}
              placeholder="เช่น ธีมไพ่นกกระจอก แตกบ่อยมาก"
              style={inputStyle(isMobile)} />
          </div>
          {/* hotgames_media_v1 */}
          <div style={{ marginBottom: 16, display:'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 12 }}>
            <div>
              <Label>🖼️ URL รูปเกม</Label>
              <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                <input value={form.image_url || ''} onChange={e => setForm({...form, image_url:e.target.value})}
                  placeholder="https://example.com/game.png"
                  style={{ ...inputStyle(isMobile), flex: 1 }} />
                {form.image_url ? (
                  <img src={form.image_url} alt="preview"
                    onError={e => { e.target.style.display='none'; }}
                    style={{ width:60, height:60, borderRadius:8, objectFit:'cover', border:'2px solid #e2e8f0', flexShrink:0 }} />
                ) : (
                  <div style={{ width:60, height:60, borderRadius:8, background:'#f1f5f9', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0, fontSize:24 }}>🎮</div>
                )}
              </div>
            </div>
            <div>
              <Label>🔗 URL เล่นเกม</Label>
              <input value={form.play_url || ''} onChange={e => setForm({...form, play_url:e.target.value})}
                placeholder="https://example.com/play"
                style={inputStyle(isMobile)} />
            </div>
          </div>
          <div style={{
            display:'flex',
            justifyContent: isMobile ? 'stretch' : 'flex-end',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 10,
          }}>
            <button onClick={() => { setShowForm(false); setEditId(null); setOtherMode(false); }}
              style={{
                padding: isMobile ? '12px 20px' : '9px 20px',
                borderRadius:8, background:'#f1f5f9', border:'none',
                cursor:'pointer', fontSize: 14, fontWeight: 500,
                order: isMobile ? 2 : 1,
              }}>ยกเลิก</button>
            <button onClick={handleSave} disabled={loading}
              style={{
                display:'flex', alignItems:'center', justifyContent: 'center', gap:6,
                padding: isMobile ? '12px 24px' : '9px 24px',
                borderRadius:8, background:'#0ea5e9', color:'#fff',
                border:'none', cursor:'pointer',
                fontSize: 14, fontWeight: 600,
                order: isMobile ? 1 : 2,
              }}>
              <Save size={14} />{loading ? 'กำลังบันทึก...' : 'บันทึก'}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div style={{
        background:'#fff', borderRadius:14,
        boxShadow:'0 1px 6px rgba(0,0,0,0.07)',
        overflow:'hidden',
      }}>
        <div style={{
          padding: isMobile ? '12px 14px' : '16px 24px',
          borderBottom:'1px solid #f1f5f9',
          display:'flex', alignItems:'center', gap: 8,
          flexDirection: isMobile ? 'column' : 'row',
          alignItems: isMobile ? 'stretch' : 'center',
        }}>
          <h2 style={{ margin:0, fontSize: isMobile ? 14 : 15, fontWeight:600, color:'#1e293b', flex:1 }}>
            รายการเกม ({filtered.length})
          </h2>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="ค้นหาเกม..."
            style={{
              padding: isMobile ? '10px 14px' : '7px 12px',
              borderRadius:8, border:'1px solid #e2e8f0',
              fontSize: isMobile ? 14 : 13,
              width: isMobile ? '100%' : 200,
              boxSizing: 'border-box',
              outline: 'none',
            }} />
        </div>

        {filtered.length === 0 ? (
          <div style={{ padding:40, textAlign:'center', color:'#94a3b8' }}>ไม่มีเกม</div>
        ) : isMobile ? (
          /* mobile: Card list */
          <div>
            {filtered.map((g, i) => (
              <GameCard key={g.id} game={g} providerColor={providerColor}
                onEdit={handleEdit} onToggle={handleToggle} onDelete={handleDelete}
                isLast={i === filtered.length - 1} />
            ))}
          </div>
        ) : (
          /* desktop: ตารางเดิม */
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#f8fafc' }}>
                {['ลำดับ','ค่าย','ชื่อเกม','RTP + Bar','คำอธิบาย','สถานะ',''].map((h,i) => (
                  <th key={i} style={{ padding:'10px 16px', textAlign:'left', fontSize:11, fontWeight:600, color:'#64748b', textTransform:'uppercase' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map(g => {
                const pc = providerColor(g.provider);
                return (
                  <tr key={g.id} style={{ borderTop:'1px solid #f1f5f9', opacity: g.is_active ? 1 : 0.5 }}
                    onMouseEnter={e => e.currentTarget.style.background='#f8fafc'}
                    onMouseLeave={e => e.currentTarget.style.background=''}>
                    <td style={{ padding:'12px 16px', color:'#94a3b8', fontSize:13 }}>{g.sort_order}</td>
                    <td style={{ padding:'12px 16px' }}>
                      <span style={{ background:pc.bg, color:pc.color, borderRadius:6, padding:'3px 10px', fontSize:12, fontWeight:600 }}>{g.provider}</span>
                    </td>
                    <td style={{ padding:'12px 16px', fontWeight:600, color:'#1e293b', fontSize:14 }}>{g.name}</td>
                    <td style={{ padding:'12px 16px' }}>
                      <div style={{ fontSize:13, color:'#0ea5e9', fontWeight:600 }}>{g.rtp}</div>
                      <div style={{ fontSize:11, color:'#64748b' }}>🎲 {g.rtp_min||85}%–{g.rtp_max||99}%</div>
                    </td>
                    <td style={{ padding:'12px 16px', fontSize:13, color:'#64748b', maxWidth:220 }}>
                      <div style={{ overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{g.description}</div>
                      {/* hotgames_media_v1 */}
                      <div style={{ display:'flex', gap:4, marginTop:4, fontSize:11 }}>
                        <span title={g.image_url || 'ไม่มีรูป'} style={{ opacity: g.image_url ? 1 : 0.25 }}>🖼️</span>
                        <span title={g.play_url || 'ไม่มีลิงก์เล่น'} style={{ opacity: g.play_url ? 1 : 0.25 }}>🔗</span>
                      </div>
                    </td>
                    <td style={{ padding:'12px 16px' }}>
                      <span style={{ background: g.is_active ? '#dcfce7' : '#f1f5f9', color: g.is_active ? '#16a34a' : '#94a3b8', borderRadius:6, padding:'3px 10px', fontSize:12 }}>
                        {g.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td style={{ padding:'12px 16px', whiteSpace:'nowrap' }}>
                      <button onClick={() => handleEdit(g)} style={{ background:'#f1f5f9', border:'none', borderRadius:6, padding:'6px 10px', cursor:'pointer', marginRight:6 }}>
                        <Edit2 size={13} color="#64748b" />
                      </button>
                      <button onClick={() => handleToggle(g)}
                        style={{ background: g.is_active ? '#fee2e2' : '#dcfce7', border:'none', borderRadius:6, padding:'6px 10px', cursor:'pointer', marginRight:6 }}>
                        <Power size={13} color={g.is_active ? '#dc2626' : '#16a34a'} />
                      </button>
                      <button onClick={() => handleDelete(g.id)} style={{ background:'#fff1f2', border:'none', borderRadius:6, padding:'6px 10px', cursor:'pointer' }}>
                        <Trash2 size={13} color="#ef4444" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

// mobile_v1: Game card สำหรับ mobile
function GameCard({ game, providerColor, onEdit, onToggle, onDelete, isLast }) {
  const pc = providerColor(game.provider);
  return (
    <div style={{
      padding: 14,
      borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
      opacity: game.is_active ? 1 : 0.6,
    }}>
      {/* Top: ลำดับ + provider + ชื่อ + status */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, marginBottom: 10 }}>
        <div style={{
          background: '#f1f5f9', color: '#64748b',
          borderRadius: 8, padding: '4px 10px',
          fontSize: 11, fontWeight: 600,
          flexShrink: 0,
        }}>
          #{game.sort_order || 0}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontWeight: 700, fontSize: 15, color: '#1e293b',
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {game.name}
          </div>
          <div style={{ display: 'flex', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
            <span style={{ background: pc.bg, color: pc.color, borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600 }}>
              {game.provider}
            </span>
            <span style={{
              background: game.is_active ? '#dcfce7' : '#f1f5f9',
              color: game.is_active ? '#16a34a' : '#94a3b8',
              borderRadius: 6, padding: '2px 8px', fontSize: 11, fontWeight: 600,
            }}>
              {game.is_active ? 'Active' : 'Inactive'}
            </span>
            {/* hotgames_media_v1 */}
            <span title={game.image_url || 'ไม่มีรูป'} style={{ fontSize:12, opacity: game.image_url ? 1 : 0.25 }}>🖼️</span>
            <span title={game.play_url || 'ไม่มีลิงก์เล่น'} style={{ fontSize:12, opacity: game.play_url ? 1 : 0.25 }}>🔗</span>
          </div>
        </div>
      </div>

      {/* RTP info */}
      <div style={{
        background: '#f0f9ff', padding: '8px 12px', borderRadius: 8,
        marginBottom: game.description ? 8 : 10,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <div>
          <div style={{ fontSize: 10, color: '#0c4a6e', fontWeight: 600, textTransform: 'uppercase' }}>RTP</div>
          <div style={{ fontSize: 16, fontWeight: 700, color: '#0ea5e9' }}>{game.rtp}</div>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 10, color: '#0c4a6e', fontWeight: 600, textTransform: 'uppercase' }}>🎲 ช่วง</div>
          <div style={{ fontSize: 13, color: '#0c4a6e', fontWeight: 600 }}>
            {game.rtp_min || 85}% – {game.rtp_max || 99}%
          </div>
        </div>
      </div>

      {/* Description */}
      {game.description && (
        <div style={{ fontSize: 13, color: '#64748b', marginBottom: 10, lineHeight: 1.4 }}>
          {game.description}
        </div>
      )}

      {/* Actions */}
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => onEdit(game)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: '#f1f5f9', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            fontSize: 13, color: '#64748b', fontWeight: 500,
          }}>
          <Edit2 size={14} /> แก้ไข
        </button>
        <button onClick={() => onToggle(game)}
          style={{
            flex: 1, padding: '10px', borderRadius: 8,
            background: game.is_active ? '#fee2e2' : '#dcfce7',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4,
            fontSize: 13, color: game.is_active ? '#dc2626' : '#16a34a', fontWeight: 500,
          }}>
          <Power size={14} /> {game.is_active ? 'ปิด' : 'เปิด'}
        </button>
        <button onClick={() => onDelete(game.id)}
          style={{
            padding: '10px 14px', borderRadius: 8,
            background: '#fff1f2', border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
          <Trash2 size={14} color="#ef4444" />
        </button>
      </div>
    </div>
  );
}

const Label = ({ children, color }) => (
  <label style={{
    fontSize: 12, fontWeight: 600,
    color: color || '#64748b',
    display: 'block', marginBottom: 4,
  }}>{children}</label>
);

const inputStyle = (isMobile) => ({
  width: '100%',
  padding: isMobile ? '12px 14px' : '9px 12px',
  borderRadius: 8,
  border: '1px solid #e2e8f0',
  fontSize: isMobile ? 16 : 14,
  boxSizing: 'border-box',
  outline: 'none',
});
